import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { UserAuth } from "../../../context/AuthContext";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import ImageUploader from "../ImageUploader/ImageUploader";
import '../ProjectForm/AdminForm.css';

const EventForm = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const { user } = UserAuth();

    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [location, setLocation] = useState('');
    const [bodyHtml, setBodyHtml] = useState('');
    const [price, setPrice] = useState('');
    const [contact, setContact] = useState('');
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            const snap = await getDoc(doc(db, "events", id));
            if (snap.exists()) {
                const data = snap.data();
                setTitle(data.title || '');
                setSubtitle(data.subtitle || '');
                setDate(data.date || '');
                setTime(data.time || '');
                setLocation(data.location || '');
                setBodyHtml(data.bodyHtml || '');
                setPrice(data.price || '');
                setContact(data.contact || '');
                setImage(data.image || null);
            }
            setLoading(false);
        })();
    }, [id, isEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = {
                title: title.trim(),
                subtitle: subtitle.trim() || null,
                date,
                time: time.trim(),
                location: location.trim(),
                bodyHtml,
                price: price.trim() || null,
                contact: contact.trim() || null,
                image: image?.url ? { url: image.url, path: image.path || null, alt: image.alt || title } : null,
                updatedAt: serverTimestamp(),
                authorEmail: user?.email || null,
            };

            if (isEdit) {
                await updateDoc(doc(db, "events", id), payload);
            } else {
                await addDoc(collection(db, "events"), { ...payload, createdAt: serverTimestamp() });
            }
            navigate('/userarea');
        } catch (err) {
            setError('Speichern fehlgeschlagen: ' + err.message);
            setSaving(false);
        }
    };

    if (loading) return <div className="admin-form-page"><p>Lädt…</p></div>;

    return (
        <div className="admin-form-page">
            <h1>{isEdit ? 'Veranstaltung bearbeiten' : 'Neue Veranstaltung'}</h1>
            <form onSubmit={handleSubmit} className="admin-form">
                <label className="admin-form-field">
                    <span>Titel</span>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="z. B. Traumhaftes Tansania" />
                </label>

                <label className="admin-form-field">
                    <span>Untertitel (optional)</span>
                    <input type="text" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="z. B. Kilimanjaro · Serengeti · Sansibar" />
                </label>

                <div className="admin-form-row">
                    <label className="admin-form-field">
                        <span>Datum</span>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
                    </label>
                    <label className="admin-form-field">
                        <span>Uhrzeit</span>
                        <input type="text" value={time} onChange={(e) => setTime(e.target.value)} placeholder="z. B. 18:00 Uhr" />
                    </label>
                </div>

                <label className="admin-form-field">
                    <span>Ort</span>
                    <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z. B. Kulturrestaurant Savanne, Lich" />
                </label>

                <label className="admin-form-field">
                    <span>Beschreibung</span>
                    <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Worum geht es bei der Veranstaltung?" />
                </label>

                <div className="admin-form-row">
                    <label className="admin-form-field">
                        <span>Preis (optional)</span>
                        <input type="text" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="z. B. 37,50 € pro Person" />
                    </label>
                    <label className="admin-form-field">
                        <span>Kontakt (optional)</span>
                        <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="z. B. Telefon, E-Mail" />
                    </label>
                </div>

                <div className="admin-form-field">
                    <span>Bild (optional)</span>
                    <ImageUploader
                        folder="events"
                        accept="image/*"
                        label=""
                        value={image}
                        onUploaded={setImage}
                        onRemove={() => setImage(null)}
                    />
                </div>

                {error && <p className="admin-form-error">{error}</p>}

                <div className="admin-form-actions">
                    <button type="button" className="admin-form-btn admin-form-btn--outline" onClick={() => navigate('/userarea')}>
                        Abbrechen
                    </button>
                    <button type="submit" className="admin-form-btn admin-form-btn--primary" disabled={saving}>
                        {saving ? 'Speichert…' : 'Speichern'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EventForm;
